'use client';

import { useRef, useState } from 'react';
import { Button, SingleSelect } from '@galapagos/design-system/web';
import { colors } from '@galapagos/design-system/colors';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MEMBER_CONTACT_MODES, type MemberContactMode } from '@galapagos/shared';
import { useShowRewards } from '../../hooks/useShowRewards';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import { useSignupFlowStore } from '../../stores/signup-flow-store';
import { callPathStep, wrapUpExitStep } from '../../wrap-up/branching';
import { useWrapUpContext } from '../../wrap-up/useWrapUpContext';
import { gated } from './gated';
import { WrapUpActions, WrapUpFrame } from './WrapUpFrame';
import { WRAP_UP_PICTURES } from './WrapUpPicture';

/** v9's card pictures, each on the fill Figma draws behind it. */
const CONTACT_MODE_THUMBNAILS = {
  call: { src: WRAP_UP_PICTURES.callThumbnail, backgroundColor: colors.mossGreen['300'] },
  chat: { src: WRAP_UP_PICTURES.chatThumbnail, backgroundColor: colors.punchPink['200'] },
} as const satisfies Record<MemberContactMode, { src: string; backgroundColor: string }>;

function isContactMode(value: string): value is MemberContactMode {
  return MEMBER_CONTACT_MODES.some((mode) => mode === value);
}

/**
 * `/call-choice/`: a welcome call, or chat in the app instead. The
 * answer is saved for the care team best effort: the member moves on whether
 * the save works or not, as the interpreter question does.
 */
function CallChoiceStep() {
  const { t } = useTranslation('signup');
  const title = t('wrapUp.callChoice.title');
  const headingRef = useStepHeading(title);
  const state = useWrapUpContext();
  const { goToStep } = useSignupNavigation();
  const setChatChosen = useSignupFlowStore((store) => store.setChatChosen);
  const api = useSignupApi();
  const [choice, setChoice] = useState<MemberContactMode>('call');
  const inFlight = useRef(false);
  // Production's rewards members see the welcome call's points in place of
  // the Recommended tag (v9 5.1 and 5.1a).
  const showRewards = useShowRewards();
  const options = [
    {
      value: 'call',
      label: t('wrapUp.callChoice.callLabel'),
      description: t('wrapUp.callChoice.callDescription'),
      tag: showRewards
        ? {
            label: t('wrapUp.callChoice.pointsTag'),
            tone: 'accent' as const,
            icon: 'gift' as const,
          }
        : { label: t('wrapUp.callChoice.recommended'), tone: 'info' as const },
    },
    {
      value: 'chat',
      label: t('wrapUp.callChoice.chatLabel'),
      description: t('wrapUp.callChoice.chatDescription'),
    },
  ];
  const moveOn = (chosen: MemberContactMode) => {
    if (chosen === 'chat') {
      setChatChosen(true);
      goToStep(wrapUpExitStep(state.ready ? state.context : undefined));
    } else if (state.ready) {
      setChatChosen(false);
      goToStep(callPathStep(state.context));
    }
  };
  const save = useMutation({
    mutationFn: (chosen: MemberContactMode) => api.saveContactMode({ contactMode: chosen }),
    onSettled: (_data, _error, chosen) => moveOn(chosen),
  });
  const onContinue = () => {
    // A call needs the wrap-up reads to pick its next step; wait for them, as before.
    if (choice === 'call' && !state.ready) return;
    if (inFlight.current) return;
    inFlight.current = true;
    save.mutate(choice);
  };
  return (
    <WrapUpFrame
      title={title}
      description={t('wrapUp.callChoice.description')}
      headingRef={headingRef}
    >
      <SingleSelect
        label={t('wrapUp.callChoice.groupLabel')}
        hideLabel
        actionId="signup.call-choice.change"
        data-testid="signup-call-choice-options"
        options={options}
        optionThumbnails={CONTACT_MODE_THUMBNAILS}
        value={choice}
        onSelect={(value) => {
          // The pending save decides where the member goes; a change now would not.
          if (!save.isPending && isContactMode(value)) setChoice(value);
        }}
      />
      <WrapUpActions>
        <Button
          size="L"
          type="Primary"
          label={t('wrapUp.callChoice.continue')}
          actionId="signup.call-choice-continue.activate"
          data-testid="signup-call-choice-continue-button"
          state={save.isPending || (choice === 'call' && !state.ready) ? 'Loading' : 'Default'}
          onPress={onContinue}
        />
      </WrapUpActions>
    </WrapUpFrame>
  );
}

export const CallChoiceScreen = gated(CallChoiceStep, 'call-choice');
